import { useEffect, useRef, useState } from 'react';
import { getPracticesList } from '../services/practices/getPractices';
import { Practice } from '../types';

// Module-level cache: practiceId → Practice (matches useCustomFields pattern)
const practiceCache = new Map<string, Practice>();
const inFlightRequests = new Map<string, Promise<Practice[]>>();

export const useBatchedPractices = (
  practiceIds: string[],
): {
  practicesMap: Record<string, Practice>;
  isLoading: boolean;
} => {
  const [practicesMap, setPracticesMap] = useState<
    Record<string, Practice>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!practiceIds.length) {
      setPracticesMap({});
      setIsLoading(false);
      return;
    }

    // Serve cached entries immediately
    const cached: Record<string, Practice> = {};
    const uncached: string[] = [];

    practiceIds.forEach((id) => {
      if (practiceCache.has(id)) {
        cached[id] = practiceCache.get(id)!;
      } else {
        uncached.push(id);
      }
    });

    if (Object.keys(cached).length > 0) {
      setPracticesMap((prev) => ({ ...prev, ...cached }));
    }

    if (uncached.length === 0) return;

    const batchKey = uncached.sort().join(',');
    let fetchPromise = inFlightRequests.get(batchKey);
    setIsLoading(true);

    if (!fetchPromise) {
      fetchPromise = getPracticesList(
        null,
        { practiceIds: uncached },
        uncached.length + 1,
      )
        .then(({ data }) => {
          const practices = data?.practices || [];
          practices.forEach((p) => practiceCache.set(p.id, p));
          inFlightRequests.delete(batchKey);
          return practices;
        })
        .catch((err) => {
          inFlightRequests.delete(batchKey);
          throw err;
        });
      inFlightRequests.set(batchKey, fetchPromise);
    }

    fetchPromise
      .then((practices) => {
        if (!mountedRef.current) return;
        const result: Record<string, Practice> = {};
        practices.forEach((p) => {
          result[p.id] = p;
        });
        setPracticesMap((prev) => ({ ...prev, ...result }));
      })
      .catch(() => {})
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceIds.join(',')]);

  return { practicesMap, isLoading };
};
